import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {INPUT_LINE_HEIGHT} from 'styles/StyleConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BaseEditableField from 'ui-library/BaseEditableField';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

const BOOLEAN_EDITOR_OPTIONS = [
  {name: 'Yes', value: true},
  {name: 'No', value: false},
];

export interface EditableBooleanDropdownFieldProps {
  selectedValue: boolean;
  onChange: (value: boolean) => void;
  isForCreate?: boolean;
  disabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
  isFullWidth?: boolean;
  shouldUseCompactDisplay?: boolean;
}

function getSelectedDisplayText(selectedValue: boolean): string | null {
  const selectedOption = BOOLEAN_EDITOR_OPTIONS.find((option) => option.value === selectedValue);
  assertAndContinue(
    selectedOption != null,
    `EditableBooleanDropdownField expects selectedValue of type "boolean", not "${typeof selectedValue}"`,
    {selectedValue: selectedValue === undefined ? '<undefined>' : selectedValue}
  );
  return selectedOption?.name ?? null;
}

function BooleanDropdown(props: EditableBooleanDropdownFieldProps): JSX.Element {
  const displayText = getSelectedDisplayText(props.selectedValue);
  return (
    <div className={css(styles.dropdown)}>
      <SelectDropdown
        disabled={props.disabled}
        displayName={displayText}
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
            {BOOLEAN_EDITOR_OPTIONS.map((optionData, i) => (
              <Option
                key={i}
                onSelect={() => {
                  props.onChange(optionData.value);
                }}
                value={optionData.value}
                isSelected={optionData.value === props.selectedValue}
              >
                {optionData.name}
              </Option>
            ))}
          </Menu>
        }
      />
    </div>
  );
}
export function EditableBooleanDropdownField(props: EditableBooleanDropdownFieldProps): JSX.Element {
  const [editingValue, setEditingValue] = useState<boolean>(props.selectedValue);
  const [isBeingEdited, setIsBeingEdited] = useState<boolean>(false);

  const displayText = getSelectedDisplayText(props.selectedValue);

  useEffect(() => {
    setEditingValue(props.selectedValue);
  }, [props.selectedValue]);

  if (props.isForCreate) {
    return <BooleanDropdown {...props} />;
  }

  const handleCancel = (): void => {
    setIsBeingEdited(false);
    setEditingValue(props.selectedValue);
  };

  const handleComplete = (): void => {
    props.onChange(editingValue);
    setEditingValue(props.selectedValue);
    setIsBeingEdited(false);
  };

  return (
    <BaseEditableField
      editor={<BooleanDropdown {...props} onChange={setEditingValue} selectedValue={editingValue} />}
      isBeingEdited={isBeingEdited}
      isWritable={!props.disabled}
      viewer={
        <div className={classNames({[css(styles.nonCompactViewer)]: !props.shouldUseCompactDisplay})}>
          {displayText}
        </div>
      }
      onCancel={handleCancel}
      onComplete={handleComplete}
      onEdit={() => {
        setIsBeingEdited(true);
      }}
      isFullWidth={props.isFullWidth}
      isCompleteEnabled={true}
      dataTestAttrs={props.dataTestAttrs}
    />
  );
}

const styles = StyleSheet.create({
  dropdown: {
    flex: '1',
    whiteSpace: 'normal',
  },
  nonCompactViewer: {
    ...INPUT_LINE_HEIGHT,
  },
});
